import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  FormControlLabel,
  Switch,
  Badge,
} from '@mui/material';
import {
  Comment,
  Add,
  Edit,
  Delete,
  Check,
  Flag,
  Person,
  Schedule,
  Warning,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

/**
 * CommentsPanel Component
 *
 * Displays all comments for a transcript with filtering and actions:
 * - View comments grouped by segment
 * - Add, edit, delete comments
 * - Filter by review status
 * - Review workflow integration
 */
const CommentsPanel = ({ transcriptId, segments, onSegmentClick }) => {
  const [comments, setComments] = useState([]);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResolved, setShowResolved] = useState(true);
  const [showOnlyReview, setShowOnlyReview] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadComments();
    loadReviewSummary();
  }, [transcriptId]);

  const loadComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getTranscriptComments(
        transcriptId,
        showResolved,
        showOnlyReview ? 'review' : null
      );
      setComments(data);
    } catch (err) {
      setError('Failed to load comments: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadReviewSummary = async () => {
    try {
      const summary = await apiService.getReviewSummary(transcriptId);
      setReviewSummary(summary);
    } catch (err) {
      console.error('Failed to load review summary:', err);
    }
  };

  useEffect(() => {
    loadComments();
  }, [showResolved, showOnlyReview]);

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await apiService.deleteComment(commentId);
      await loadComments();
      await loadReviewSummary();
    } catch (err) {
      setError('Failed to delete comment: ' + err.message);
    }
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment);
    setEditText(comment.comment_text);
  };

  const handleUpdateComment = async () => {
    try {
      setUpdating(true);
      await apiService.updateComment(editingComment.id, {
        comment_text: editText,
      });
      setEditingComment(null);
      setEditText('');
      await loadComments();
    } catch (err) {
      setError('Failed to update comment: ' + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleResolveComment = async (comment) => {
    try {
      await apiService.updateComment(comment.id, {
        is_resolved: !comment.is_resolved,
      });
      await loadComments();
      await loadReviewSummary();
    } catch (err) {
      setError('Failed to update comment: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getSegmentText = (segmentIndex) => {
    if (segments && segments[segmentIndex]) {
      return segments[segmentIndex].text || '';
    }
    return `Segment ${segmentIndex}`;
  };

  const groupCommentsBySegment = () => {
    const grouped = {};
    comments.forEach((comment) => {
      if (!grouped[comment.segment_index]) {
        grouped[comment.segment_index] = [];
      }
      grouped[comment.segment_index].push(comment);
    });
    return grouped;
  };

  const groupedComments = groupCommentsBySegment();
  const segmentIndices = Object.keys(groupedComments).map(Number).sort((a, b) => a - b);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Comment />
          <Typography variant="h6">Comments</Typography>
          <Chip label={`${comments.length} comments`} size="small" />
          {reviewSummary && reviewSummary.unresolved_reviews > 0 && (
            <Badge badgeContent={reviewSummary.unresolved_reviews} color="error">
              <Chip
                label="Needs Review"
                size="small"
                color="warning"
                icon={<Warning />}
              />
            </Badge>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
            />
          }
          label="Show resolved"
        />
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyReview}
              onChange={(e) => setShowOnlyReview(e.target.checked)}
            />
          }
          label="Review items only"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {reviewSummary && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Review Status: {reviewSummary.unresolved_reviews} unresolved, {reviewSummary.resolved_reviews} resolved
          {reviewSummary.segment_count > 0 && (
            <> • {reviewSummary.segment_count} segments need review</>
          )}
        </Alert>
      )}

      <List sx={{ maxHeight: 500, overflow: 'auto' }}>
        {segmentIndices.map((segmentIndex) => (
          <React.Fragment key={segmentIndex}>
            <ListItem
              sx={{
                bgcolor: 'action.hover',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.selected' },
              }}
              onClick={() => onSegmentClick && onSegmentClick(segmentIndex)}
            >
              <ListItemText
                primary={
                  <Typography variant="subtitle2" fontWeight="bold">
                    Segment {segmentIndex + 1}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" noWrap>
                    "{getSegmentText(segmentIndex).substring(0, 60)}..."
                  </Typography>
                }
              />
              <Chip
                label={groupedComments[segmentIndex].length}
                size="small"
                sx={{ mr: 1 }}
              />
            </ListItem>

            {groupedComments[segmentIndex].map((comment) => (
              <ListItem
                key={comment.id}
                sx={{
                  pl: 4,
                  borderLeft: comment.needs_review ? '4px solid' : 'none',
                  borderLeftColor: 'warning.main',
                  bgcolor: comment.is_resolved ? 'action.disabledBackground' : 'transparent',
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      {comment.needs_review && (
                        <Chip
                          label="Review"
                          size="small"
                          color="warning"
                          icon={<Flag />}
                        />
                      )}
                      {comment.is_resolved && (
                        <Chip
                          label="Resolved"
                          size="small"
                          color="success"
                          icon={<Check />}
                        />
                      )}
                      {comment.author_name && (
                        <Chip
                          label={comment.author_name}
                          size="small"
                          icon={<Person />}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {comment.comment_text}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule sx={{ fontSize: 14 }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(comment.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {comment.needs_review && (
                      <Tooltip title={comment.is_resolved ? 'Mark as unresolved' : 'Mark as resolved'}>
                        <IconButton
                          size="small"
                          onClick={() => handleResolveComment(comment)}
                        >
                          <Check color={comment.is_resolved ? 'success' : 'disabled'} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit comment">
                      <IconButton
                        size="small"
                        onClick={() => handleEditComment(comment)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete comment">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteComment(comment.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            <Divider />
          </React.Fragment>
        ))}
      </List>

      {comments.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No comments yet.
        </Typography>
      )}

      {/* Edit Comment Dialog */}
      <Dialog
        open={editingComment !== null}
        onClose={() => !updating && setEditingComment(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Comment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            fullWidth
            multiline
            rows={4}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            disabled={updating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingComment(null)} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateComment}
            variant="contained"
            disabled={updating || !editText.trim()}
            startIcon={updating ? <CircularProgress size={16} /> : <Edit />}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CommentsPanel;
